/**
 * Date formatting utilities for the application
 * All dates use IST (Indian Standard Time, UTC+5:30)
 */

/**
 * Get current date in IST as YYYY-MM-DD string
 * @returns {string} - Current IST date in YYYY-MM-DD format
 */
export const getISTDateString = () => {
  const now = new Date();
  // IST is UTC+5:30
  const istOffset = 5.5 * 60 * 60 * 1000; // 5:30 hours in milliseconds
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
  const istDate = new Date(utcTime + istOffset);
  return `${istDate.getFullYear()}-${String(istDate.getMonth() + 1).padStart(2, '0')}-${String(istDate.getDate()).padStart(2, '0')}`;
};

/**
 * Format a date string from YYYY-MM-DD to DD-MM-YYYY
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date in DD-MM-YYYY format
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  
  // Handle ISO date strings
  const date = dateStr.split('T')[0];
  
  // Check if already in correct format or invalid
  if (!date || date.length < 10) return dateStr || '-';
  
  const parts = date.split('-');
  if (parts.length !== 3) return dateStr;
  
  const [year, month, day] = parts;
  return `${day}-${month}-${year}`;
};

/**
 * Format a JS Date object to DD-MM-YYYY (timezone-safe, uses local parts)
 * @param {Date} d - Date object
 * @returns {string} - DD-MM-YYYY
 */
export const formatDateObj = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${d.getFullYear()}`;
};

/**
 * Format a date for display with optional time
 * @param {string} dateStr - ISO date string
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} - Formatted date string
 */
export const formatDateTime = (dateStr, includeTime = false) => {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    if (includeTime) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    }
    
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

/**
 * Format month-year string (YYYY-MM to MM-YYYY or Month YYYY)
 * @param {string} monthYear - Month year in YYYY-MM format
 * @param {boolean} useWords - Use month name instead of number
 * @returns {string} - Formatted month year
 */
export const formatMonthYear = (monthYear, useWords = false) => {
  if (!monthYear) return '-';
  
  const [year, month] = monthYear.split('-');
  
  if (useWords) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  }
  
  return `${month}-${year}`;
};

/**
 * Get today's date in DD-MM-YYYY format
 * @returns {string} - Today's date
 */
export const getTodayFormatted = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Convert DD-MM-YYYY to YYYY-MM-DD (for API calls)
 * @param {string} dateStr - Date in DD-MM-YYYY format
 * @returns {string} - Date in YYYY-MM-DD format
 */
export const toAPIDateFormat = (dateStr) => {
  if (!dateStr) return '';
  
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  
  // Check if already in YYYY-MM-DD format
  if (parts[0].length === 4) return dateStr;
  
  const [day, month, year] = parts;
  return `${year}-${month}-${day}`;
};
